/**
 * PostHogEditor - Configuration editor for PostHog browser analytics
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, FormControl, InputLabel, Link, MenuItem, Select, TextField } from '@mui/material';
import type { PlatformConfig, PostHogConfig } from '../../types/analytics.types';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

const POSTHOG_US_HOST = 'https://us.i.posthog.com';
const POSTHOG_EU_HOST = 'https://eu.i.posthog.com';

type PostHogRegion = 'us' | 'eu' | 'custom';

const getRegionFromHost = (host?: string): PostHogRegion => {
  if (host === POSTHOG_US_HOST) {
    return 'us';
  }
  if (host === POSTHOG_EU_HOST) {
    return 'eu';
  }
  return 'custom';
};

interface PostHogEditorProps {
  config: PlatformConfig;
  onChange: (config: PlatformConfig, name?: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  showSaveButton?: boolean;
  onSave?: (config: PlatformConfig, name?: string) => void;
  name?: string;
}

export const PostHogEditor: React.FC<PostHogEditorProps> = ({
  config,
  onChange,
  disabled = false,
  readOnly = false,
  showSaveButton = false,
  onSave,
  name,
}) => {
  const posthogConfig = config as PostHogConfig;
  const [localConfig, setLocalConfig] = useState<PostHogConfig>(posthogConfig);
  const [localName, setLocalName] = useState(name || '');
  const selectedRegion = getRegionFromHost(localConfig.host || POSTHOG_US_HOST);

  useEffect(() => {
    setLocalConfig(posthogConfig);
    setLocalName(name || '');
  }, [posthogConfig, name]);

  const handleChange = useCallback(
    (field: keyof PostHogConfig, value: string) => {
      const newConfig = { ...localConfig, [field]: value };
      setLocalConfig(newConfig);
      if (!showSaveButton) {
        onChange(newConfig, localName);
      }
    },
    [localConfig, localName, onChange, showSaveButton]
  );

  const handleRegionChange = useCallback(
    (region: PostHogRegion) => {
      if (region === 'us') {
        handleChange('host', POSTHOG_US_HOST);
        return;
      }
      if (region === 'eu') {
        handleChange('host', POSTHOG_EU_HOST);
        return;
      }
      handleChange('host', localConfig.host || '');
    },
    [handleChange, localConfig.host]
  );

  const handleSave = useCallback(() => {
    onSave?.(localConfig, localName);
  }, [localConfig, localName, onSave]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Copy the <strong>Project token</strong> from PostHog Settings &gt; General &gt; Project token &amp; ID.
        Do not paste the Project ID.
      </Alert>

      <TextField
        label="Project token"
        placeholder="phc_..."
        value={localConfig.projectToken || ''}
        onChange={(event) => handleChange('projectToken', event.target.value)}
        disabled={disabled || readOnly}
        fullWidth
        required
        sx={{ mb: 2 }}
        helperText={
          <>
            Find this token in{' '}
            <Link href="https://posthog.com/docs/libraries/js" target="_blank" rel="noopener">
              PostHog SDK setup
            </Link>
            . It is labeled "Project token" and is safe for browser SDK use.
          </>
        }
        slotProps={{ htmlInput: { 'data-testid': ANALYTICS_SETTINGS_TEST_IDS.editorField('posthog', 'projectToken') } }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Region</InputLabel>
        <Select
          value={selectedRegion}
          onChange={(event) => handleRegionChange(event.target.value as PostHogRegion)}
          disabled={disabled || readOnly}
          label="Region"
        >
          <MenuItem value="us">US Cloud</MenuItem>
          <MenuItem value="eu">EU Cloud</MenuItem>
          <MenuItem value="custom">Self-hosted / custom host</MenuItem>
        </Select>
      </FormControl>

      {selectedRegion === 'custom' && (
        <TextField
          label="Custom API host"
          placeholder="https://posthog.example.com"
          value={localConfig.host || ''}
          onChange={(event) => handleChange('host', event.target.value)}
          disabled={disabled || readOnly}
          fullWidth
          sx={{ mb: 2 }}
          helperText="Use your self-hosted PostHog host. Cloud projects should use the Region selector above."
        />
      )}

      {showSaveButton && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !localConfig.projectToken}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.editorSave('posthog')}
          >
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};
